import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import Counter from './Counter';
import BackToDashboardButton from './BackToDashboardButton';
import { FiSearch, FiStar, FiTruck, FiCheckCircle, FiCalendar, FiPlus, FiPhone, FiX, FiSave, FiArrowLeft, FiCheck } from 'react-icons/fi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar as faCalendarRegular } from '@fortawesome/free-regular-svg-icons';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import './VendorManagement.css'; // Make sure you have this CSS file
import { StandardSearch, StandardScrollStyles, highlightMatch, performSmartSearch, standardTextSizes } from '../styles/standardStyles';
import { AuthContext } from '../contexts/AuthContext';
import { useTranslate } from '../translations/useTranslate';

// API Base URL configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';

// Shared CalendarSelect component (module scope) to preserve state across re-renders
const CalendarSelect = ({ options, value, onChange, width = 140 }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const current = options.find(o => o.value === value) || options[0];
    return (
        <div
            className="calendar-select"
            ref={ref}
            style={{ width }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            <button
                type="button"
                className="calendar-select-trigger"
                onClick={() => setOpen(v => !v)}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <span>{current?.label}</span>
                <span className="calendar-select-caret">▾</span>
            </button>
            {open && (
                <div
                    className="calendar-select-menu"
                    style={{ width }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="calendar-select-list standard-scrollbar">
                        {options.map(opt => (
                            <div
                                key={opt.value}
                                className={`calendar-select-option${opt.value === value ? ' selected' : ''}`}
                                onClick={() => { onChange(opt.value); setOpen(false); }}
                            >
                                <span>{opt.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const VendorSelectModal = ({ vendors, onSelect, onCancel }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const filtered = performSmartSearch(vendors, searchTerm, ['name', 'category']);

    return (
        <div className="vm-modal-overlay">
            <div className="vm-modal-content">
                <StandardScrollStyles />
                <div className="vm-modal-header">
                    <h2 style={standardTextSizes.lg}>Select Vendor</h2>
                    <button onClick={onCancel} className="vm-modal-close-btn"><FiX /></button>
                </div>
                <StandardSearch
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                    onClearSearch={clearSearch}
                    placeholder="Search vendors..."
                    inputRef={searchInputRef}
                />
                <div className="vm-modal-list" style={{ maxHeight: '300px', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    {filtered.map(vendor => (
                        <div key={vendor.id} className="vm-modal-list-item" onClick={() => onSelect(vendor)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={standardTextSizes.base}>
                                {highlightMatch(vendor.name, searchTerm)}
                            </span>
                            {vendor.creditBalance > 0 && (
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#2563eb', marginLeft: '16px' }}>
                                    ₹{vendor.creditBalance.toLocaleString()}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const AddCreditModal = ({ vendor, onAdd, onCancel, defaultPaymentTerms = 7, onNotify }) => {
    const [amount, setAmount] = useState('');
    const [paymentTerms, setPaymentTerms] = useState(defaultPaymentTerms);
    const [customDays, setCustomDays] = useState('');
    const [notes, setNotes] = useState('');
    const [showCustom, setShowCustom] = useState(false);
    const [currentBalance, setCurrentBalance] = useState(vendor.creditBalance || 0);
    const [errorMessage, setErrorMessage] = useState('');

    const paymentTermOptions = [7, 14, 21, 30];

    const handleAddClick = () => {
        setErrorMessage(''); // Clear previous errors
        const numericAmount = parseFloat(amount) || 0;
        const termsToUse = showCustom ? parseInt(customDays) : paymentTerms;
        
        // Allow saving if there's an amount to add OR just updating current balance
        if (numericAmount > 0 || currentBalance !== vendor.creditBalance) {
            if (numericAmount > 0 && (!termsToUse || termsToUse <= 0)) {
                onNotify?.('error', 'Please enter valid payment terms.');
                return;
            }
            onAdd(vendor.id, numericAmount, termsToUse, notes, currentBalance);
        } else {
            onNotify?.('error', 'Please enter a credit amount or update the current balance.');
        }
    };

    return (
        <div className="vm-modal-overlay">
            <div className="vm-modal-content">
                <div className="vm-modal-header">
                    <h2>Add Credit for {vendor.name}</h2>
                    <button onClick={onCancel} className="vm-modal-close-btn"><FiX /></button>
                </div>
                {(errorMessage || vendor._error) && (
                    <div style={{
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fecaca',
                        color: '#dc2626',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        {errorMessage || vendor._error}
                    </div>
                )}
                <div className="ev-input-group" style={{ marginBottom: '16px' }}>
                    <label>Current Balance</label>
                    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: '100%' }}>
                        <span style={{ 
                            position: 'absolute', 
                            left: '12px', 
                            fontSize: '16px', 
                            fontWeight: '500', 
                            color: '#374151',
                            pointerEvents: 'none'
                        }}>₹</span>
                        <input 
                            type="number"
                            value={currentBalance}
                            onChange={(e) => setCurrentBalance(Number(e.target.value) || 0)}
                            style={{
                                width: '100%',
                                padding: '12px 12px 12px 28px',
                                fontSize: '16px',
                                fontWeight: '500',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                outline: 'none'
                            }}
                        />
                    </div>
                </div>
                
                <div className="ev-input-group">
                    <label>Credit Amount *</label>
                    <input 
                        type="number" 
                        placeholder="e.g., 5000" 
                        value={amount} 
                        onChange={(e) => setAmount(e.target.value)} 
                    />
                </div>

                <div className="ev-input-group">
                    <label>Payment Terms (Days) *</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                        {paymentTermOptions.map(days => (
                            <button
                                key={days}
                                type="button"
                                className={`vm-filter-pill ${!showCustom && paymentTerms === days ? 'active' : ''}`}
                                onClick={() => {
                                    setShowCustom(false);
                                    setPaymentTerms(days);
                                }}
                            >
                                {days} Days
                            </button>
                        ))}
                        <button
                            type="button"
                            className={`vm-filter-pill ${showCustom ? 'active' : ''}`}
                            onClick={() => setShowCustom(true)}
                        >
                            Custom
                        </button>
                    </div>
                    {showCustom && (
                        <input 
                            type="number" 
                            placeholder="Enter Custom Days" 
                            value={customDays} 
                            onChange={(e) => setCustomDays(e.target.value)} 
                        />
                    )}
                </div>

                <div className="ev-input-group">
                    <label>Notes (Optional)</label>
                    <textarea 
                        placeholder="Add Any Notes About This Credit..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                    />
                </div>

                <div className="vm-modal-actions">
                    <button className="vm-modal-btn-secondary" onClick={onCancel}>Cancel</button>
                    <button className="vm-modal-btn-primary" onClick={handleAddClick}>Save</button>
                </div>
            </div>
        </div>
    );
};

const StarRating = ({ rating, onRatingChange }) => {
    const [hover, setHover] = useState(0);
    return (
        <div className="ev-star-rating">
            {[...Array(5)].map((star, index) => {
                const ratingValue = index + 1;
                return (
                    <label key={index}>
                        <input type="radio" name="rating" value={ratingValue} onClick={() => onRatingChange(ratingValue)} />
                        <FiStar className="ev-star" color={ratingValue <= (hover || Number(rating || 0)) ? "#ffc107" : "#e4e5e9"} fill={ratingValue <= (hover || Number(rating || 0)) ? "#ffc107" : "none"} onMouseEnter={() => setHover(ratingValue)} onMouseLeave={() => setHover(0)} />
                    </label>
                );
            })}
            <span className="ev-star-rating-text">{Number(rating || 0)} stars</span>
        </div>
    );
};

const EditVendor = ({ vendor, onSave, onCancel, onDelete, onNotify }) => {
    const [formData, setFormData] = useState(vendor);
    // Custom calendar state (match StockInForm)
    const [showLastOrderPicker, setShowLastOrderPicker] = useState(false);
    const [lastOrderViewDate, setLastOrderViewDate] = useState(() => {
        return formData.lastOrder ? new Date(formData.lastOrder) : new Date();
    });
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    const openLastOrderPicker = () => {
        const cur = formData.lastOrder;
        setLastOrderViewDate(cur ? new Date(cur) : new Date());
        setShowLastOrderPicker(true);
    };
    const closeLastOrderPicker = () => setShowLastOrderPicker(false);
    const navigateLastOrderMonth = (direction) => {
        const d = new Date(lastOrderViewDate);
        d.setMonth(d.getMonth() + direction);
        setLastOrderViewDate(d);
    };
    const handleLastOrderClick = (day) => {
        const sel = new Date(lastOrderViewDate.getFullYear(), lastOrderViewDate.getMonth(), day);
        const ymd = sel.toLocaleDateString('en-CA');
        setFormData(prev => ({ ...prev, lastOrder: ymd }));
        setShowLastOrderPicker(false);
    };
    const renderLastOrderCalendar = () => {
        const daysInMonth = getDaysInMonth(lastOrderViewDate);
        const firstDay = getFirstDayOfMonth(lastOrderViewDate);
        const days = [];
        for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(
                <div key={day} className="calendar-day" onClick={() => handleLastOrderClick(day)}>
                    {day}
                </div>
            );
        }
        return days;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Validate percentage fields to not exceed 100
        if (name === 'onTime' || name === 'quality') {
            // Allow empty string (when user clears the field)
            if (value === '') {
                setFormData(prev => ({ ...prev, [name]: 0 }));
                return;
            }
            
            const numValue = parseFloat(value);
            if (numValue > 100) {
                // Don't update if value exceeds 100
                return;
            }
            // Also ensure it's not negative
            if (numValue < 0) {
                setFormData(prev => ({ ...prev, [name]: 0 }));
                return;
            }
        }
        
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    // Using module-scoped CalendarSelect defined above
    const handleRatingChange = (newRating) => {
        setFormData(prev => ({ ...prev, rating: newRating }));
    };

    const handleSave = () => {
        // Validate required fields
        if (!formData.name || !formData.name.trim()) {
            onNotify?.('error', 'Please enter the vendor name');
            return;
        }
        
        if (!formData.category) {
            onNotify?.('error', 'Please select a vendor category');
            return;
        }
        
        // Call the parent's save handler
        onSave(formData);
    };

    return (
        <div className="ev-page-container">
            <header className="ev-header">
                <button
                    type="button"
                    aria-label="Back"
                    onClick={onCancel}
                    className="ev-back-btn"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <h1>{vendor.id && vendor.id <= 2147483647 ? 'Edit Vendor' : 'Add Vendor'}</h1>
                <button className="ev-save-btn" onClick={handleSave}>Save</button>
            </header>
            <div className="ev-content">
                <div className="ev-section"><h2 className="ev-section-title">Basic Information</h2><div className="ev-input-group"><label>Vendor Name *</label><input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g., Fresh Farms Supply" /></div><div className="ev-input-group"><label>Description</label><textarea name="description" value={formData.description || ''} onChange={handleInputChange} placeholder="Brief description about this vendor..." rows="3" style={{ resize: 'vertical' }} /></div></div>
                <div className="ev-section"><h2 className="ev-section-title">Category</h2><div className="vm-filters">{['Wholesale', 'Dairy', 'Meat', 'Seafood', 'Vegetables', 'Fruits'].map(cat => (<button key={cat} className={`vm-filter-pill ${formData.category === cat ? 'active' : ''}`} onClick={() => setFormData(prev => ({ ...prev, category: cat }))}>{cat}</button>))}</div></div>
                <div className="ev-section"><h2 className="ev-section-title">Your Rating</h2><StarRating rating={formData.rating} onRatingChange={handleRatingChange} /><p className="ev-rating-prompt">Rate this vendor based on your experience</p></div>
                <div className="ev-section"><h2 className="ev-section-title">Performance Metrics</h2><div className="ev-input-group"><label>On-Time Delivery (%)</label><input type="number" name="onTime" value={formData.onTime === 0 ? '' : formData.onTime} onChange={handleInputChange} placeholder="0" min="0" max="100" step="0.01" /></div><div className="ev-input-group"><label>Quality Score (%)</label><input type="number" name="quality" value={formData.quality === 0 ? '' : formData.quality} onChange={handleInputChange} placeholder="0" min="0" max="100" step="0.01" /></div><div className="ev-input-group"><label>Last Delivery Date</label><div className="date-picker-wrap"><input type="date" name="lastOrder" value={formData.lastOrder} onChange={handleInputChange} /><button type="button" className="date-picker-button" aria-label="Open calendar" onClick={openLastOrderPicker}><FontAwesomeIcon icon={faCalendarRegular} /></button></div></div></div>

            {/* Last Delivery Date calendar overlay (same type as StockIn) */}
            {showLastOrderPicker && (
                <div className="date-picker-overlay" onClick={closeLastOrderPicker}>
                    <div className="date-picker-popup" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <div className="calendar-header">
                            <button onClick={() => navigateLastOrderMonth(-1)}>‹</button>
                            <div className="calendar-header-controls">
                                <CalendarSelect
                                    options={monthNames.map((m, i) => ({ label: m, value: i }))}
                                    value={lastOrderViewDate.getMonth()}
                                    onChange={(val) => { const d = new Date(lastOrderViewDate); d.setMonth(val); setLastOrderViewDate(d); }}
                                    width={130}
                                />
                                <CalendarSelect
                                    options={Array.from({ length: 21 }, (_, k) => new Date().getFullYear() - 10 + k).map(y => ({ label: String(y), value: y }))}
                                    value={lastOrderViewDate.getFullYear()}
                                    onChange={(val) => { const d = new Date(lastOrderViewDate); d.setFullYear(val); setLastOrderViewDate(d); }}
                                    width={90}
                                />
                            </div>
                            <button onClick={() => navigateLastOrderMonth(1)}>›</button>
                        </div>
                        <div className="calendar-weekdays">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                                <div key={d} className="weekday">{d}</div>
                            ))}
                        </div>
                        <div className="calendar-grid">
                            {renderLastOrderCalendar()}
                        </div>
                    </div>
                </div>
            )}
                <div className="ev-section"><h2 className="ev-section-title">Contact Information</h2><div className="ev-input-group"><label>Phone Number</label><input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+91 98765 43210" maxLength="10" /></div><div className="ev-input-group"><label>Email</label><input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="vendor@example.com" /></div><div className="ev-input-group"><label>Address</label><textarea name="address" value={formData.address || ''} onChange={handleInputChange} placeholder="Street, City, State, PIN" rows="2" style={{ resize: 'vertical' }} /></div></div>
                
                {/* Delete Section - Only show for existing vendors */}
                {vendor.id && vendor.id <= 2147483647 && (
                    <button 
                        className="ev-delete-btn"
                        onClick={() => onDelete && onDelete(vendor.id, vendor.name)}
                        style={{
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            padding: '14px 24px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            width: '100%',
                            marginTop: '20px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#fecaca';
                            e.target.style.color = '#b91c1c';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#fee2e2';
                            e.target.style.color = '#dc2626';
                        }}
                    >
                        Delete Vendor
                    </button>
                )}
            </div>
        </div>
    );
};

const VendorManagement = ({ onNavigateBack, isSelectingMode, onSelectVendor, initialCategoryFilter = 'All' }) => {

    const { businessId } = useContext(AuthContext);
    const { language, t } = useTranslate();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);
    const [activeFilter, setActiveFilter] = useState(initialCategoryFilter);
    const [editingVendor, setEditingVendor] = useState(null);
    const [vendorForCredit, setVendorForCredit] = useState(null);
    const [isVendorSelectOpen, setIsVendorSelectOpen] = useState(false);
    const [statusModal, setStatusModal] = useState(null);
    const statusModalTimerRef = useRef(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [defaultPaymentTerms, setDefaultPaymentTerms] = useState(7);
    const [editableTotalCredit, setEditableTotalCredit] = useState(0);
    const [isEditingCredit, setIsEditingCredit] = useState(false);

    useEffect(() => {
        return () => {
            if (statusModalTimerRef.current) {
                clearTimeout(statusModalTimerRef.current);
                statusModalTimerRef.current = null;
            }
        };
    }, []);

    const showStatusModal = (type, message, autoCloseMs = 1500) => {
        setStatusModal({ type, message });

        if (statusModalTimerRef.current) {
            clearTimeout(statusModalTimerRef.current);
            statusModalTimerRef.current = null;
        }

        if (autoCloseMs && autoCloseMs > 0) {
            statusModalTimerRef.current = setTimeout(() => {
                setStatusModal(null);
                statusModalTimerRef.current = null;
            }, autoCloseMs);
        }
    };
    
    // Category translation helper
    const getCategoryLabel = (category) => {
        const categoryKey = category.toLowerCase();
        if (category === 'All') return t('vendorCategories.all');
        return t(`vendorCategories.${categoryKey}`) || category;
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    // Fetch payment terms from settings
    useEffect(() => {
        const fetchPaymentTerms = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/settings/${businessId}?keys=vendors.default_payment_terms_days`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: Failed to fetch payment terms`);
                }

                const data = await response.json();
                if (data.success && data.data.length > 0) {
                    const terms = parseInt(data.data[0].value);
                    setDefaultPaymentTerms(terms);
                }
            } catch (error) {
                console.error('Error fetching payment terms:', error);
            }
        };

        if (businessId) {
            fetchPaymentTerms();
        }
    }, [businessId]);

    // Update activeFilter when initialCategoryFilter changes
    useEffect(() => {
        setActiveFilter(initialCategoryFilter);
    }, [initialCategoryFilter]);

    // Fetch vendors from API
    useEffect(() => {
        const fetchVendors = async () => {
            try {
                setLoading(true);
                // Fetching vendors from API

                const currentLanguage = language || localStorage.getItem('selectedLanguage') || 'en';

                // Build API URL with category filter and language
                let apiUrl = `${API_BASE_URL}/vendor-management/vendors?language=${currentLanguage}`;
                if (activeFilter !== 'All') {
                    // Convert category to lowercase to match database (Meat -> meat, Dairy -> dairy, etc.)
                    apiUrl += `&category=${activeFilter.toLowerCase()}`;
                }
                

                const response = await fetch(apiUrl, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                
                // Check if response is OK before parsing
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('❌ Server error:', response.status, errorText);
                    throw new Error(`Server error: ${response.status}`);
                }
                
                const data = await response.json();

                // API Response received

                if (data.success) {
                    
                    // Transform API data to match component expectations
                    const transformedVendors = data.data.map(vendor => ({
                        id: vendor.vendor_id,
                        name: vendor.name,
                        description: vendor.description || '',
                        category: vendor.category || 'Wholesale',
                        rating: Number(vendor.user_rating || vendor.average_rating || 0) || 0,
                        onTime: vendor.on_time_delivery_rate || 0,
                        quality: vendor.quality_score || 0,
                        lastOrder: vendor.last_order_date || new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
                        phone: vendor.contact_phone || '',
                        email: vendor.contact_email || '',
                        address: vendor.address || '',
                        creditBalance: Number(vendor.credit_balance) || 0,
                        totalSpend: Number(vendor.total_spend || 0) || 0,
                        totalOrders: vendor.total_purchase_orders || 0
                    }));
                    
                    
                    setVendors(transformedVendors);
                } else {
                    console.error('Failed to fetch vendors:', data.error);
                    // If no vendors exist, try to setup sample vendors
                    const setupResponse = await fetch('/api/vendor-management/setup-categories', {
                        method: 'POST',
                        headers: { 
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    });
                    const setupData = await setupResponse.json();

                    if (setupData.success) {
                        // Retry fetching vendors
                        window.location.reload();
                    } else {
                        setVendors([]);
                    }
                }
            } catch (error) {
                console.error('❌ Error fetching vendors:', error);
                setVendors([]);
            } finally {
                setLoading(false);
            }
        };

        fetchVendors();
    }, [activeFilter, language]); // Re-fetch when filter or language changes

    const filteredVendors = useMemo(() => {
        // Use smart search with priority-based filtering
        return searchTerm.trim()
            ? performSmartSearch(vendors, searchTerm, ['name', 'category', 'description'])
            : vendors;
    }, [vendors, searchTerm]);

    const totalCredit = useMemo(() => {
        const calculated = vendors.reduce((sum, vendor) => {
            const credit = Number(vendor.creditBalance) || 0;
            return sum + credit;
        }, 0);
        if (!isEditingCredit) {
            setEditableTotalCredit(calculated);
        }
        return calculated;
    }, [vendors, isEditingCredit]);

    const handleSaveVendor = async (vendorToSave) => {
        try {
            // Check if this is a new vendor (temporary ID from Date.now()) or existing vendor
            const isNewVendor = vendorToSave.id > 2147483647; // If ID is larger than max integer, it's a timestamp

            if (isNewVendor) {
                // Create new vendor
                const createData = {
                    name: vendorToSave.name,
                    description: vendorToSave.description,
                    category: vendorToSave.category,
                    contactPhone: vendorToSave.phone,
                    contactEmail: vendorToSave.email,
                    contactWhatsapp: vendorToSave.phone, // Use phone as whatsapp for now
                    address: vendorToSave.address,
                    averageRating: vendorToSave.rating,
                    qualityScore: vendorToSave.quality,
                    userId: localStorage.getItem('userId') || 1
                };

                const response = await fetch(`${API_BASE_URL}/vendor-management/vendors`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(createData)
                });

                // Check response status before parsing
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('❌ Server error creating vendor:', response.status, errorText);
                    throw new Error(`Server error: ${response.status}`);
                }

                const result = await response.json();

                if (result.success) {
                    // Add the new vendor with the real ID from database
                    const newVendor = {
                        ...vendorToSave,
                        id: result.data.vendor_id,
                        // Map backend response to frontend format
                        category: result.data.category,
                        onTime: result.data.on_time_delivery_rate || 0,
                        creditBalance: result.data.credit_balance || 0,
                        rating: result.data.average_rating || vendorToSave.rating,
                        quality: result.data.quality_score || vendorToSave.quality
                    };
                    setVendors(prev => [...prev, newVendor]);

                    showStatusModal('success', 'Vendor created successfully');

                    // Close edit form
                    setEditingVendor(null);
                } else {
                    console.error('❌ Failed to create vendor:', result.error);
                    showStatusModal('error', 'Failed to create vendor: ' + result.error);
                }
            } else {
                // Update existing vendor
                const updateData = {
                    name: vendorToSave.name,
                    description: vendorToSave.description,
                    category: vendorToSave.category,
                    contact_phone: vendorToSave.phone,
                    contact_email: vendorToSave.email,
                    address: vendorToSave.address,
                    average_rating: vendorToSave.rating,
                    quality_score: vendorToSave.quality
                };

                const response = await fetch(`${API_BASE_URL}/vendor-management/vendors/${vendorToSave.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(updateData)
                });

                // Check response status before parsing
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('❌ Server error updating vendor:', response.status, errorText);
                    throw new Error(`Server error: ${response.status}`);
                }

                const result = await response.json();

                if (result.success) {
                    // Update local state
                    setVendors(prev => prev.map(v => v.id === vendorToSave.id ? vendorToSave : v));

                    showStatusModal('success', 'Vendor updated successfully');

                    // Close edit form
                    setEditingVendor(null);
                } else {
                    console.error('Failed to update vendor:', result.error);
                    showStatusModal('error', 'Failed to update vendor: ' + result.error);
                }
            }
        } catch (error) {
            console.error('Error saving vendor:', error);
            showStatusModal('error', 'Error saving vendor: ' + error.message);
        }
    };

    const performDeleteVendor = async (vendorId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/vendor-management/vendors/${vendorId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            // Check response status before parsing
            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ Server error deleting vendor:', response.status, errorText);
                throw new Error(`Server error: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.success) {
                // Remove vendor from local state
                setVendors(prev => prev.filter(vendor => vendor.id !== vendorId));
                showStatusModal('success', 'Vendor deleted successfully');
                // Close edit form if this vendor was being edited
                setEditingVendor(null);
            } else {
                console.error('Failed to delete vendor:', result.error);
                showStatusModal('error', 'Failed to delete vendor: ' + result.error);
            }
        } catch (error) {
            console.error('Error deleting vendor:', error);
            showStatusModal('error', 'Error deleting vendor: ' + error.message);
        }
    };

    const handleDeleteVendor = (vendorId, vendorName) => {
        setConfirmDelete({ vendorId, vendorName });
    };

    const handleAddVendorClick = () => {
        const newVendorTemplate = {
            id: Date.now(),
            name: '',
            description: '',
            category: 'Vegetables',
            rating: 0,
            onTime: 0,
            quality: 0,
            lastOrder: new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
            phone: '',
            email: '',
            address: '',
            creditBalance: 0
        };
        setEditingVendor(newVendorTemplate);
    };

    const handleSelectVendorForCredit = (vendor) => {
        setVendorForCredit(vendor);
        setIsVendorSelectOpen(false);
    };

    const handleAddCredit = async (vendorId, amount, paymentTermsDays, notes, newCurrentBalance) => {
        try {
            // If only updating current balance (amount is 0), skip the API call and update locally
            if (amount === 0 && newCurrentBalance !== undefined) {
                setVendors(prev =>
                    prev.map(v => v.id === vendorId ? { 
                        ...v, 
                        creditBalance: newCurrentBalance 
                    } : v)
                );
                showStatusModal('success', `Current balance updated to ₹${newCurrentBalance.toLocaleString()}`);
                setVendorForCredit(null);
                return;
            }

            const response = await fetch(`${API_BASE_URL}/vendor-credits`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'X-Business-Id': businessId
                },
                body: JSON.stringify({
                    vendor_id: vendorId,
                    credit_amount: amount,
                    payment_terms_days: paymentTermsDays,
                    notes: notes,
                    created_by: businessId
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ Server error adding credit:', response.status, errorText);
                throw new Error(`Failed to add credit. Please check your connection and try again.`);
            }

            const result = await response.json();

            if (result.success) {
                // Update local vendor state with new credit
                // If newCurrentBalance is provided and different from original, use it; otherwise add amount to current balance
                setVendors(prev =>
                    prev.map(v => v.id === vendorId ? { 
                        ...v, 
                        creditBalance: newCurrentBalance !== undefined && amount > 0 ? newCurrentBalance + amount : (newCurrentBalance !== undefined ? newCurrentBalance : (v.creditBalance || 0) + amount)
                    } : v)
                );
                
                showStatusModal('success', `Credit of ₹${amount.toLocaleString()} added successfully. Due in ${paymentTermsDays} days.`);
                setVendorForCredit(null);
            } else {
                console.error('Failed to add credit:', result.error);
                showStatusModal('error', 'Failed to add credit: ' + result.error);
            }
        } catch (error) {
            console.error('Error adding credit:', error);
            showStatusModal('error', 'Error adding credit: ' + error.message);
        }
    };

    const closeModals = () => {
        setIsVendorSelectOpen(false);
        setVendorForCredit(null);
    };

    return (
        <>
            <StandardScrollStyles />

            {editingVendor ? (
                <EditVendor
                    vendor={editingVendor}
                    onSave={handleSaveVendor}
                    onCancel={() => setEditingVendor(null)}
                    onDelete={handleDeleteVendor}
                    onNotify={showStatusModal}
                />
            ) : (
                <>
                    <div className="vm-page-container">
                <div className="vm-content">
                    <header className="vm-header" style={{ position: 'relative' }}>
                        <button
                            onClick={onNavigateBack}
                            style={{
                                position: 'absolute',
                                left: '0',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '8px',
                                paddingLeft: '0px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#000000',
                                fontSize: '24px'
                            }}
                        >
                            <FiArrowLeft />
                        </button>
                        <h1 style={standardTextSizes.xl}>{t('vendorManagement.title')}</h1>
                    </header>
                    <StandardSearch
                        style={{marginBottom:'12px'}}
                        searchTerm={searchTerm}
                        onSearchChange={handleSearchChange}
                        onClearSearch={clearSearch}
                        placeholder={t('vendorManagement.search_placeholder')}
                        inputRef={searchInputRef}
                    />
                    <div className="vm-filters">
                        {['All', 'Wholesale', 'Dairy', 'Meat', 'Seafood', 'Vegetables', 'Fruits'].map(filter => {
                            const isActive = activeFilter === filter;
                            return (
                                <button
                                    key={filter}
                                    className={`vm-filter-pill ${isActive ? 'active' : ''}`}
                                    onClick={() => setActiveFilter(filter)}
                                >
                                    {getCategoryLabel(filter)}
                                </button>
                            );
                        })}
                    </div>
                    <>
                        <div className="vm-total-spend-card-modified">
                            <div className="vm-total-spend-info">
                                <span className="vm-total-spend-label">Today Total Expense</span>
                                <div className="vm-total-spend-amount">
                                    ₹ 18,200
                                </div>
                            </div>

                            <div className="vm-total-credit-info">
                                <span className="vm-total-credit-label">Total Credit</span>
                                <span className="vm-total-credit-amount">
                                    ₹{totalCredit.toLocaleString()}
                                </span>
                            </div>

                            <button className="vm-add-credit-btn" onClick={() => setIsVendorSelectOpen(true)}>
                                <FiPlus /> {t('vendorManagement.add_credit')}
                            </button>
                        </div>
                    </>
                    <div className="vm-vendor-list">
                        {loading ? (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '60vh',
                                padding: '40px 20px',
                                gap: '20px'
                            }}>
                                <div style={{ position: 'relative', width: '95px', height: '95px' }}>
                                    <img 
                                        src="/invexis-logo.png"
                                        alt="Invexis"
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: '70px',
                                            height: '70px',
                                            borderRadius: '50%',
                                            backgroundColor: '#fff',
                                            padding: '0px',
                                            pointerEvents: 'none',
                                            objectFit: 'contain',
                                            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                            zIndex: 1
                                        }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '70px',
                                        height: '70px',
                                        border: '6px solid #000000',
                                        borderTop: '6px solid #f9c806',
                                        borderRight: '6px solid #f9c806',
                                        borderRadius: '50%',
                                        animation: 'vendorSpin 1s linear infinite',
                                        zIndex: 16
                                    }}></div>
                                </div>
                                <p style={{
                                    color: '#666',
                                    fontSize: '16px',
                                    textAlign: 'center',
                                    margin: 0
                                }}>Loading Vendors...</p>
                                <style>{`
                                    @keyframes vendorSpin { 
                                        0% { transform: translate(-50%, -50%) rotate(0deg); } 
                                        100% { transform: translate(-50%, -50%) rotate(360deg); } 
                                    }
                                `}</style>
                            </div>
                        ) : filteredVendors.length > 0 ? (
                            filteredVendors.map(vendor => (
                                <div
                                    key={vendor.id}
                                    className="vm-vendor-card"
                                    onClick={(e) => {
                                        
                                        // Transform vendor data for CreateReorder
                                        const vendorForSelection = {
                                            vendor_id: vendor.id,
                                            name: vendor.name,
                                            description: vendor.description,
                                            vendor_category: vendor.category.toLowerCase(),
                                            category: vendor.category,
                                            average_rating: vendor.rating,
                                            contact_phone: vendor.phone,
                                            contact_email: vendor.email,
                                            total_purchase_orders: vendor.totalOrders,
                                            on_time_delivery_rate: vendor.onTime,
                                            quality_score: vendor.quality,
                                            address: vendor.address
                                        };
                                        
                                        if (isSelectingMode && onSelectVendor) {

                                            // Store in localStorage IMMEDIATELY (primary backup method)
                                            localStorage.setItem('pendingVendorSelection', JSON.stringify(vendorForSelection));
                                            
                                            // Call parent handler (secondary method)
                                            try {
                                                onSelectVendor(vendorForSelection);
                                            } catch (error) {
                                                console.error('   ❌ Error calling onSelectVendor:', error);
                                            }
                                            
                                            // NOTE: Don't navigate here - App.js handleVendorSelect will handle it
                                        } else {
                                            // NOT in selection mode - but still save to localStorage for reorder page
                                            localStorage.setItem('pendingVendorSelection', JSON.stringify(vendorForSelection));
                                            // Open edit modal as normal
                                            setEditingVendor(vendor);
                                        }
                                    }}
                                >
                                    <div className="vm-vendor-header">
                                        <div className="vm-vendor-title">
                                            <h2 style={{ ...standardTextSizes.lg, color: '#000000' }}>
                                                {highlightMatch(vendor.name, searchTerm)}
                                            </h2>
                                            <p style={standardTextSizes.sm}>
                                                {highlightMatch(vendor.description, searchTerm)}
                                            </p>
                                        </div>
                                        <div className="vm-vendor-rating">
                                            <FiStar className="vm-star-icon" />
                                            <span>{vendor.rating}</span>
                                        </div>
                                    </div>
                                    <div className="vm-vendor-stats"><span className="vm-stat-item"><FiTruck /> {vendor.onTime}% {t('vendorManagement.on_time')}</span><span className="vm-stat-item"><FiCheckCircle /> {vendor.quality}% {t('vendorManagement.quality')}</span><span className="vm-stat-item"><FiCalendar /> {new Date(vendor.lastOrder).toLocaleDateString('en-GB')}</span></div>
                                    <div className="vm-vendor-actions">
                                        <button className="vm-contact-btn" onClick={(e) => {
                                            if (!isSelectingMode) {
                                                e.stopPropagation();
                                            }
                                        }}>
                                            <FiPhone /> {t('vendorManagement.contact')}
                                        </button>
                                        <button className="vm-details-btn" onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingVendor(vendor);
                                        }}>
                                            {t('vendorManagement.view_details')}
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="vm-no-vendors-container">
                                <p className="vm-no-vendors-message">{t('vendorManagement.no_vendors_found')}: "{getCategoryLabel(activeFilter)}"</p>
                                <p className="vm-no-vendors-suggestion">{t('vendorManagement.select_all')}</p>
                            </div>
                        )}
                    </div>
                    <button className="vm-add-vendor-btn" onClick={handleAddVendorClick}><FiPlus /> {t('vendorManagement.add_vendor')}</button>
                </div>
            </div>
            {isVendorSelectOpen && <VendorSelectModal vendors={vendors} onSelect={handleSelectVendorForCredit} onCancel={closeModals} />}
            {vendorForCredit && !isVendorSelectOpen && (
                <AddCreditModal
                    vendor={vendorForCredit}
                    onAdd={handleAddCredit}
                    onCancel={closeModals}
                    defaultPaymentTerms={defaultPaymentTerms}
                    onNotify={(type, message) => showStatusModal(type, message)}
                />
            )}

                </>
            )}

            {/* Confirm Delete Modal */}
            {confirmDelete && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '10px'
                    }}
                    onClick={() => setConfirmDelete(null)}
                >
                    <div
                        style={{
                            width: 'min(420px, 92vw)',
                            background: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            padding: '24px',
                            textAlign: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            marginBottom: '10px',
                            color: '#111827'
                        }}>
                            Delete Vendor?
                        </div>
                        <div style={{
                            fontSize: '14px',
                            color: '#374151',
                            marginBottom: '18px'
                        }}>
                            Are you sure you want to delete "{confirmDelete.vendorName}"? This action cannot be undone.
                        </div>
                        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                            <button
                                onClick={() => setConfirmDelete(null)}
                                style={{
                                    flex: 1,
                                    padding: '12px 14px',
                                    backgroundColor: '#e5e7eb',
                                    color: '#111827',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    const { vendorId } = confirmDelete;
                                    setConfirmDelete(null);
                                    await performDeleteVendor(vendorId);
                                }}
                                style={{
                                    flex: 1,
                                    padding: '12px 14px',
                                    backgroundColor: '#000000',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Status Modal (success/error) */}
            {statusModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '10px'
                    }}
                    onClick={() => setStatusModal(null)}
                >
                    <div
                        style={{
                            width: 'min(400px, 90vw)',
                            background: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            padding: '24px',
                            textAlign: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                            {statusModal.type === 'success' ? (
                                <FiCheck size={36} color="#22c55e" strokeWidth={3} />
                            ) : (
                                <div
                                    style={{
                                        width: '56px',
                                        height: '56px',
                                        borderRadius: '50%',
                                        border: '3px solid #ef4444',
                                        color: '#ef4444',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '28px',
                                        fontWeight: '800',
                                        lineHeight: 1
                                    }}
                                >
                                    !
                                </div>
                            )}
                        </div>
                        <div style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '20px',
                            color: '#1f2937',
                            whiteSpace: 'pre-line'
                        }}>
                            {statusModal.message}
                        </div>
                        <button
                            onClick={() => setStatusModal(null)}
                            style={{
                                padding: '10px 24px',
                                backgroundColor: '#000',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default VendorManagement;